import { Injectable, inject } from '@angular/core';
import { DeviceDetectorService as NgxDeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, Observable, fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  os: string;
  osVersion: string;
  browser: string;
  browserVersion: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  orientation: 'portrait' | 'landscape';
  userAgent: string;
}

export type DeviceOS = 'ios' | 'android' | 'mac' | 'windows' | 'linux' | 'unknown';

@Injectable({
  providedIn: 'root',
})
export class DeviceDetectorService {
  private _deviceDetector = inject(NgxDeviceDetectorService);
  private _orientation = new BehaviorSubject<'portrait' | 'landscape'>(
    this._getCurrentOrientation()
  );

  constructor() {
    // Detectar cambios de orientación
    fromEvent(window, 'resize')
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this._orientation.next(this._getCurrentOrientation());
      });
  }

  /**
   * Obtiene información completa del dispositivo actual
   */
  getDeviceInfo(): DeviceInfo {
    return {
      isMobile: this._deviceDetector.isMobile(),
      isTablet: this._deviceDetector.isTablet(),
      isDesktop: this._deviceDetector.isDesktop(),
      os: this._deviceDetector.os,
      osVersion: this._deviceDetector.os_version,
      browser: this._deviceDetector.browser,
      browserVersion: this._deviceDetector.browser_version,
      deviceType: this.getDeviceType(),
      orientation: this._orientation.getValue(),
      userAgent: this._deviceDetector.userAgent,
    };
  }

  /**
   * Verifica si el dispositivo actual es móvil
   */
  isMobile(): boolean {
    return this._deviceDetector.isMobile();
  }

  /**
   * Verifica si el dispositivo actual es una tablet
   */
  isTablet(): boolean {
    return this._deviceDetector.isTablet();
  }

  /**
   * Verifica si el dispositivo actual es un desktop
   */
  isDesktop(): boolean {
    return this._deviceDetector.isDesktop();
  }

  /**
   * Verifica si el dispositivo actual es un iPhone/iPad
   */
  isIOS(): boolean {
    return this._deviceDetector.os.toLowerCase() === 'ios';
  }

  /**
   * Verifica si el dispositivo actual es un dispositivo Android
   */
  isAndroid(): boolean {
    return this._deviceDetector.os.toLowerCase() === 'android';
  }

  /**
   * Verifica si el dispositivo actual es una Mac
   */
  isMac(): boolean {
    return (
      this._deviceDetector.os.toLowerCase() === 'mac' ||
      this._deviceDetector.os.toLowerCase() === 'macos' ||
      this._deviceDetector.os.toLowerCase() === 'mac os' ||
      this._deviceDetector.os.toLowerCase() === 'mac os x'
    );
  }

  /**
   * Verifica si el dispositivo actual es un PC con Windows
   */
  isWindows(): boolean {
    return this._deviceDetector.os.toLowerCase() === 'windows';
  }

  /**
   * Verifica si el dispositivo actual es Linux
   */
  isLinux(): boolean {
    return this._deviceDetector.os.toLowerCase() === 'linux';
  }

  /**
   * Obtiene el sistema operativo del dispositivo actual
   */
  getOS(): DeviceOS {
    if (this.isIOS()) return 'ios';
    if (this.isAndroid()) return 'android';
    if (this.isMac()) return 'mac';
    if (this.isWindows()) return 'windows';
    if (this.isLinux()) return 'linux';

    return 'unknown';
  }

  /**
   * Obtiene el tipo de dispositivo (mobile, tablet, desktop)
   */
  getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    if (this._deviceDetector.isMobile()) return 'mobile';
    if (this._deviceDetector.isTablet()) return 'tablet';
    return 'desktop';
  }

  /**
   * Obtiene la orientación actual del dispositivo
   */
  getOrientation(): 'portrait' | 'landscape' {
    return this._orientation.getValue();
  }

  /**
   * Observable que emite cuando cambia la orientación del dispositivo
   */
  get orientation$(): Observable<'portrait' | 'landscape'> {
    return this._orientation.asObservable();
  }

  /**
   * Detecta la orientación actual del dispositivo
   */
  private _getCurrentOrientation(): 'portrait' | 'landscape' {
    if (window.innerHeight > window.innerWidth) {
      return 'portrait';
    }
    return 'landscape';
  }
}
